import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  cn,
  Form as FormContextProvider,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@kalos/ui';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  type DefaultValues,
  type FieldValues,
  type Resolver,
  useForm,
  type UseFormReturn,
  useWatch,
} from 'react-hook-form';
import { type ZodType } from 'zod';

import { bottomSheetAboveKeyboardClass, bottomSheetBodyClass } from '../keyboardAwareClasses';

type FilterSheetProps<TValues extends FieldValues> = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  /** The filters currently applied to the list. The sheet starts from these each time it opens. */
  values: TValues;
  /** What Reset returns every field to. */
  defaultValues: TValues;
  /** Optional rules across fields, such as a date range that needs both ends. Checked on Apply. */
  schema?: ZodType<TValues>;
  onApply: (values: TValues) => void;
  /** The screen renders its own fields, bound to the staged form. */
  children?: (form: UseFormReturn<TValues>) => ReactNode;
  /**
   * Optional drill-in panels, for filters with long option lists: the sheet opens on a list of
   * rows (each with its current value), and a row opens its own panel with Back. Staging, Apply,
   * Cancel and Reset work as without panels. Sheets that don't pass this are unchanged.
   */
  panels?: ReadonlyArray<FilterSheetPanel<TValues>>;
};

export type FilterSheetPanel<TValues extends FieldValues> = {
  id: string;
  label: string;
  /** The row's current value from the staged fields: "Any", one choice, or "3 selected". */
  summary: (values: TValues) => string;
  /** The panel's fields. Call `back` to return to the list, as a single choice does once picked. */
  render: (form: UseFormReturn<TValues>, back: () => void) => ReactNode;
};

/** The list of drill-in rows, each showing its staged value. */
const FilterPanelRows = <TValues extends FieldValues>({
  form,
  panels,
  onOpenPanel,
}: {
  form: UseFormReturn<TValues>;
  panels: ReadonlyArray<FilterSheetPanel<TValues>>;
  onOpenPanel: (id: string) => void;
}) => {
  const values = useWatch({ control: form.control }) as TValues;
  return (
    <ul aria-label="Filters" className="divide-border -mx-4 divide-y border-y">
      {panels.map((panel) => (
        <li key={panel.id}>
          <button
            type="button"
            onClick={() => onOpenPanel(panel.id)}
            className="active:bg-muted flex min-h-14 w-full items-center gap-3 px-4 py-3 text-left"
          >
            <span className="flex-1 text-sm font-medium">{panel.label}</span>
            <span className="text-muted-foreground truncate text-sm">{panel.summary(values)}</span>
            <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" aria-hidden />
          </button>
        </li>
      ))}
    </ul>
  );
};

/**
 * Bottom sheet holding a list's filters. Edits are staged: nothing reloads until Apply.
 * Reset clears the staged fields; Cancel, or dismissing with unapplied changes, asks first.
 * Pair it with `FilterSummaryRow`, the one control on the screen that opens it.
 */
export const FilterSheet = <TValues extends FieldValues>({
  open,
  onOpenChange,
  title,
  description = 'Changes apply when you press Apply filters.',
  values,
  defaultValues,
  schema,
  onApply,
  children,
  panels,
}: FilterSheetProps<TValues>) => {
  const formId = useId();
  const form = useForm<TValues>({
    defaultValues: values as DefaultValues<TValues>,
    resolver: schema ? (zodResolver(schema) as Resolver<TValues>) : undefined,
  });

  const [openPanelId, setOpenPanelId] = useState<string | null>(null);
  const openPanel = panels?.find((panel) => panel.id === openPanelId);

  // Start from the applied filters (and the panel list) each time the sheet opens, not on
  // every render.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      form.reset(values);
      setOpenPanelId(null);
    }
    wasOpen.current = open;
  }, [form, open, values]);

  // Discarding unapplied changes is asked inside this sheet, never in a dialog stacked on it.
  const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false);
  useEffect(() => {
    if (!open) setIsConfirmingDiscard(false);
  }, [open]);

  const requestClose = useCallback(() => {
    if (form.formState.isDirty) {
      setIsConfirmingDiscard(true);
      return;
    }
    onOpenChange(false);
  }, [form.formState.isDirty, onOpenChange]);

  const applyFilters = form.handleSubmit((stagedValues) => {
    onApply(stagedValues);
    onOpenChange(false);
  });

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => (nextOpen ? onOpenChange(true) : requestClose())}
    >
      <SheetContent
        side="bottom"
        // The side padding is on the scrolling body, so the full-width option rows (`-mx-4`) and
        // Back's `-ml-2` stay inside it instead of widening it.
        className={cn(bottomSheetAboveKeyboardClass, 'rounded-t-xl px-0 md:px-0')}
        bodyClassName={cn(bottomSheetBodyClass, 'px-4 md:px-6')}
        closeButtonClassName="hidden"
        footerActions={
          isConfirmingDiscard ? (
            <div className="flex flex-col gap-2 pb-[calc(env(safe-area-inset-bottom))]">
              <Button type="button" variant="destructive" onClick={() => onOpenChange(false)}>
                Discard changes
              </Button>
              <Button type="button" variant="ghost" onClick={() => setIsConfirmingDiscard(false)}>
                Keep editing
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 pb-[calc(env(safe-area-inset-bottom))]">
              <Button type="button" variant="outline" onClick={requestClose}>
                Cancel
              </Button>
              <Button type="submit" form={formId}>
                Apply filters
              </Button>
            </div>
          )
        }
      >
        {isConfirmingDiscard ? (
          <SheetHeader className="space-y-2 pb-4 text-left">
            <SheetTitle>Discard filter changes?</SheetTitle>
            <SheetDescription>
              The changes you made here haven&apos;t been applied to the list.
            </SheetDescription>
          </SheetHeader>
        ) : (
          <>
            <SheetHeader className="flex-row items-center justify-between gap-2 space-y-0 text-left">
              {openPanel ? (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    className="-ml-2 gap-1"
                    onClick={() => setOpenPanelId(null)}
                  >
                    <ChevronLeft className="h-4 w-4" aria-hidden />
                    Back to {title}
                  </Button>
                  <SheetTitle className="min-w-0 truncate">{openPanel.label}</SheetTitle>
                </>
              ) : (
                <>
                  <SheetTitle className="min-w-0 truncate">{title}</SheetTitle>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => form.reset(defaultValues, { keepDefaultValues: true })}
                  >
                    Reset filters
                  </Button>
                </>
              )}
            </SheetHeader>
            <SheetDescription className="pb-4">{description}</SheetDescription>
            <FormContextProvider {...form}>
              <form id={formId} className="flex flex-col gap-4" onSubmit={applyFilters}>
                {openPanel ? (
                  openPanel.render(form, () => setOpenPanelId(null))
                ) : (
                  <>
                    {children?.(form)}
                    {panels && (
                      <FilterPanelRows form={form} panels={panels} onOpenPanel={setOpenPanelId} />
                    )}
                  </>
                )}
                {form.formState.errors.root?.message && (
                  <p role="alert" className="text-destructive text-sm">
                    {form.formState.errors.root.message}
                  </p>
                )}
              </form>
            </FormContextProvider>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
